import { deriveGraph } from '@agavi/blocks';
import { ArrowRight, ChevronDown, Flag, Map as MapIcon, Plus } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { MiniMap } from '../experience/builder/mini-map';

import { FlowPickerMenu, type PickerOption, type PickerSection } from './flow-picker-menu';
import { PHONE_QUERY } from './use-viewport';

import type { Task } from '@/types/experience';
import type { EdgeCategory, FlowAction } from '@agavi/blocks';

import ModalShell from '@/components/modal-shell';
import { Button } from '@/components/ui/button';
import { TextField } from '@/components/ui/text-field';

interface FlowTargetTaskPickerProps {
  tasks: Task[];
  currentTaskId: string;
  /** The event's current routing action (next / end / goto{id}). Stay is handled by the card's own
   * cycle button, so this component is only rendered for a "go" action. */
  action: FlowAction;
  onChange: (action: FlowAction) => void;
  isInvalid?: boolean;
  /** Create a new NAMED pathway task off the current task and return its id (null on failure). */
  onCreateDetourTask?: (names: { pathwayName: string; taskTitle: string }) => Promise<string | null>;
  /** Whether this event may end the experience at all — only the correct through-line. Gates the
   * forward option where it would otherwise resolve to an end. */
  allowEnd?: boolean;
  /** The event's category — colours the pick-on-map flow line to match Flow (correct/incorrect/etc.). */
  category?: EdgeCategory;
  special?: boolean;
  inline?: boolean;
}

/**
 * Custom, accessible target picker for a flow "go" action — styled like our other dropdown menus
 * (not the browser's native <select> popup). A compact trigger opens a portaled combobox: a filter
 * input plus a grouped listbox (Main flow, then each named Pathway), with Next / End and the
 * create-new / pick-on-map commands. Fully keyboard operable — type to filter, Up/Down/Home/End to
 * move (virtual focus via aria-activedescendant), Enter to choose, Escape or click-outside to close
 * (focus returns to the trigger). Writes the same `task.flow` the board does, so the two stay in sync.
 */
export default function FlowTargetTaskPicker({ tasks, currentTaskId, action, onChange, isInvalid, onCreateDetourTask, allowEnd = false, category, special, inline }: FlowTargetTaskPickerProps) {
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [nameOpen, setNameOpen] = useState(false);

  const { main, pathways } = useMemo(() => {
    const graph = deriveGraph(tasks);
    const byId = new Map(tasks.map((tk) => [tk.id, tk]));
    const mainList = graph.spine
      .map((id, i) => ({ task: byId.get(id), num: i + 1 }))
      .filter((x): x is { task: Task; num: number } => !!x.task && x.task.id !== currentTaskId);
    const order: string[] = [];
    const m = new Map<string, { name: string; members: Task[] }>();
    for (const tk of tasks) {
      if (!tk.pathwayId) continue;
      if (!m.has(tk.pathwayId)) { m.set(tk.pathwayId, { name: tk.pathwayName || t('experience.builder.pathwayUnnamed'), members: [] }); order.push(tk.pathwayId); }
      if (tk.id !== currentTaskId) m.get(tk.pathwayId)!.members.push(tk);
    }
    return { main: mainList, pathways: order.map((id) => m.get(id)!).filter((p) => p.members.length > 0) };
  }, [tasks, currentTaskId, t]);

  const gotoId = action.type === 'goto' ? action.targetTaskId : null;
  const untitled = t('experience.builder.a11y.untitled');

  /**
   * One forward option, and it is positional.
   *
   * `next` means the task after this one, and where there is none — the end of a spine, the edge of
   * a pathway — the run stops. Move the task and it follows; grow the pathway and it carries on into
   * the new task. Hence the name: what it currently does is shown in the label, in brackets, because
   * "End Experience" on a spine task is true only until the task stops being last.
   *
   * There used to be a manual "End Experience" beside it, writing `end`. It was removed because it
   * almost never meant anything it appeared to mean. `normalizeFlowConfig` strips `end` from every
   * non-correct slot on load; `normalizeTerminals` demotes it to `next` on any spine task that is no
   * longer last; and the board draws `end`, `stay` and a fall-off with the same End cap. That left
   * one real behaviour — on a BRANCH, an `end` keeps ending even after the pathway gains a task
   * below it — which is invisible on screen and not what a teacher picking "End Experience" is
   * asking for. If a pathway ever needs to say "deliberately finished", that belongs to the pathway,
   * with a cap that looks different, not to an action indistinguishable from the default.
   */
  const hasNext = useMemo(() => deriveGraph(tasks).nextOf[currentTaskId] != null, [tasks, currentTaskId]);
  const defaultLabel = hasNext ? t('task.flow.card.defaultNext') : t('task.flow.card.defaultEnd');

  /**
   * Every goto this picker emits carries the destination's title.
   *
   * Snapshotted here because here is the only moment it is knowable: once the task is deleted it
   * is filtered out of the payload entirely, leaving the client an id it can never resolve. Same
   * device as `conditionLabels`, and refreshed on load while the target lives.
   */
  const gotoAction = (id: string): FlowAction => ({
    type: 'goto',
    targetTaskId: id,
    targetTitle: tasks.find((tk) => tk.id === id)?.title || undefined,
  });

  // Current-selection label for the closed trigger.
  const triggerLabel = useMemo(() => {
    if (action.type === 'end') return t('task.flow.card.endExperience');
    if (action.type === 'goto' && gotoId) {
      const tk = tasks.find((x) => x.id === gotoId);
      if (!tk) return t('task.flow.targetInvalid');
      const num = deriveGraph(tasks).spine.indexOf(gotoId);
      return num >= 0 ? t('task.flow.card.goToTaskN', { num: num + 1, title: tk.title || untitled }) : (tk.title || untitled);
    }
    return defaultLabel;
  }, [action, gotoId, tasks, t, untitled, defaultLabel]);

  // Grouped sections, unfiltered: FlowPickerMenu filters on each option's `text`.
  const sections = useMemo(() => {
    const secs: PickerSection[] = [];
    // Top group = the quick actions, before the task list, so the common destinations don't require
    // scrolling past every task.
    const top: PickerOption[] = [
      {
        key: 'next',
        text: defaultLabel,
        icon: hasNext
          ? <ArrowRight className='h-4 w-4 shrink-0 text-ws-muted' aria-hidden='true' />
          : <Flag className='h-4 w-4 shrink-0 text-ws-muted' aria-hidden='true' />,
        selected: action.type === 'next' || action.type === 'end',
        // Where Default already means "the run stops", it is only offerable on the correct path:
        // leaving because you got something wrong is not a route the board can draw, and allowing
        // it here would make "only the correct path ends this" a rule bypassed by taking the
        // forward option instead of End.
        ...(hasNext || allowEnd ? {} : { disabled: t('task.flow.card.endNotAllowed') }),
      },
      { key: 'map', text: t('task.flow.card.pickOnMap'), icon: <MapIcon className='h-4 w-4 shrink-0 text-ws-muted' aria-hidden='true' /> },
    ];
    if (onCreateDetourTask) {
      top.push({ key: 'create', text: t('task.flow.card.createPathwayTask'), icon: <Plus className='h-4 w-4 shrink-0 text-ws-muted' aria-hidden='true' /> });
    }
    secs.push({ options: top });

    const mainOpts = main.map(({ task, num }) => ({
      key: task.id,
      text: t('task.flow.card.goToTaskN', { num, title: task.title || untitled }),
      selected: gotoId === task.id,
    }));
    if (mainOpts.length) secs.push({ header: t('experience.builder.mainFlow'), options: mainOpts });
    for (const pw of pathways) {
      secs.push({
        header: pw.name,
        options: pw.members.map((task) => ({ key: task.id, text: task.title || untitled, selected: gotoId === task.id })),
      });
    }
    return secs;
  }, [main, pathways, allowEnd, onCreateDetourTask, t, untitled, hasNext, defaultLabel, action.type, gotoId]);

  function choose(key: string) {
    if (key === 'next') { onChange(hasNext ? { type: 'next' } : { type: 'end' }); return; }
    if (key === 'map') { setMapOpen(true); return; }
    if (key === 'create') { setNameOpen(true); return; }
    onChange(gotoAction(key));
  }

  return (
    <>
      <FlowPickerMenu
        sections={sections}
        onChoose={choose}
        ariaLabel={t('task.flow.targetTaskPicker.label')}
        filterPlaceholder={t('task.flow.card.filterTasks')}
        noMatchesLabel={t('task.flow.card.noMatches')}
        className={inline ? 'flex-1' : ''}
        renderTrigger={(p) => (
          <button
            {...p}
            type='button'
            aria-label={t('task.flow.targetTaskPicker.label')}
            className={
              inline
                ? `flex min-w-0 flex-1 items-center gap-0.5 truncate rounded bg-transparent px-1 py-0.5 -mx-1 text-left text-xs italic hover:bg-black/[0.04] dark:hover:bg-white/[0.06] focus:bg-black/[0.04] dark:focus:bg-white/[0.06] focus:outline-none ${isInvalid ? 'text-ws-warning-fg' : 'text-ws-muted'}`
                : `flex min-w-0 flex-1 items-center gap-1 rounded border bg-ws-btn px-2.5 py-1.5 text-left text-sm text-ws-text focus:outline-none ${isInvalid ? 'border-ws-warning-fg text-ws-warning-fg' : 'border-ws-border focus:border-ws-primary'}`
            }
          >
            <span className='min-w-0 flex-1 truncate'>{triggerLabel}</span>
            <ChevronDown className='h-3.5 w-3.5 shrink-0 opacity-60' aria-hidden='true' />
          </button>
        )}
      />

      {mapOpen && (
        <MapPickModal tasks={tasks} currentTaskId={currentTaskId} selectedId={gotoId} category={category} special={special} onPick={(id) => {
          onChange(gotoAction(id));
          // On a phone (touch, OR a narrow/short viewport — covers BOTH portrait and landscape) keep the
          // map OPEN after picking so you can SEE the connection (ring + flow line) and fix a mis-tap
          // without reopening. On a comfortable desktop the hover-preview shows it before you click, so
          // close as before. Close via the X / Escape when done.
          // Coarse OR phone-sized, and the size half comes from the shared query rather than
          // from numbers of its own: this used to say 768px and 540px, near-misses of the 767
          // and 599 everything else uses, which is how three ideas of "small" got into one app.
          const phoneish = window.matchMedia(`(pointer: coarse), ${PHONE_QUERY}`).matches;
          if (!phoneish) setMapOpen(false);
        }} onClose={() => setMapOpen(false)} />
      )}
      {nameOpen && onCreateDetourTask && (
        <CreatePathwayDialog
          tasks={tasks}
          busy={creating}
          onCancel={() => setNameOpen(false)}
          onConfirm={async (names) => {
            setCreating(true);
            try { const id = await onCreateDetourTask(names); if (id) { onChange(gotoAction(id)); setNameOpen(false); } }
            finally { setCreating(false); }
          }}
        />
      )}
    </>
  );
}

/** Name-the-stub dialog for "Create a new pathway task": prefilled + editable pathway and task names,
 * with live collision detection against existing pathway names so the stub is findable and unique. */
function CreatePathwayDialog({ tasks, busy, onConfirm, onCancel }: {
  tasks: Task[];
  busy: boolean;
  onConfirm: (names: { pathwayName: string; taskTitle: string }) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const taken = useMemo(() => new Set(tasks.filter((tk) => tk.pathwayId && (tk.pathwayName ?? '').trim()).map((tk) => tk.pathwayName!.trim().toLowerCase())), [tasks]);
  const defaultName = useMemo(() => { let n = 1; let name = t('experience.builder.defaultPathwayName', { n }); while (taken.has(name.toLowerCase())) { n += 1; name = t('experience.builder.defaultPathwayName', { n }); } return name; }, [taken, t]);
  const [pathwayName, setPathwayName] = useState(defaultName);
  const [taskTitle, setTaskTitle] = useState(t('experience.newTask'));
  const firstRef = useRef<HTMLInputElement>(null);
  // ModalShell focuses the first real control on open, in a rAF. Select the default name in a rAF
  // of our own so it lands after that focus rather than racing it — a teacher opens this to type
  // their own pathway name, and having to clear the default first is the difference.
  useEffect(() => {
    const raf = requestAnimationFrame(() => firstRef.current?.select());
    return () => cancelAnimationFrame(raf);
  }, []);

  const trimmed = pathwayName.trim();
  const collides = !!trimmed && taken.has(trimmed.toLowerCase());
  const valid = !!trimmed && !collides && !!taskTitle.trim();

  return createPortal(
    // z-[120]: this opens from the Flow panel, which is itself a z-[110] full-screen dialog on a
    // phone — a default-layer modal would render underneath the panel that opened it.
    <ModalShell title={t('task.flow.card.createPathwayTitle')} onClose={onCancel} maxWidth='max-w-sm' zClass='z-[120]'>
      <div className='flex flex-col gap-3'>
        <label className='flex flex-col gap-1 text-sm'>
          <span className='font-medium text-ws-text'>{t('task.flow.card.pathwayNameLabel')}</span>
          <TextField
            variant='prose'
            value={pathwayName}
            onChange={setPathwayName}
            inputRef={firstRef}
            hasError={collides}
            ariaLabel={t('task.flow.card.pathwayNameLabel')}
          />
          {collides && <span role='alert' className='text-xs text-ws-warning-fg'>{t('task.flow.card.pathwayNameTaken')}</span>}
        </label>
        <label className='flex flex-col gap-1 text-sm'>
          <span className='font-medium text-ws-text'>{t('task.flow.card.taskNameLabel')}</span>
          <TextField
            variant='prose'
            value={taskTitle}
            onChange={setTaskTitle}
            ariaLabel={t('task.flow.card.taskNameLabel')}
          />
        </label>
        <div className='mt-1 flex justify-end gap-2'>
          <Button variant='subtle' onClick={onCancel}>{t('common.cancel')}</Button>
          <Button
            variant='primary'
            disabled={!valid}
            busy={busy}
            onClick={() => valid && onConfirm({ pathwayName: trimmed, taskTitle: taskTitle.trim() })}
          >
            {t('task.flow.card.createPathwayConfirm')}
          </Button>
        </div>
      </div>
    </ModalShell>,
    document.body,
  );
}

/**
 * Modal wrapping the shared MiniMap in pick mode — click a task on the overview to set it as the
 * goto target. The task itself is excluded (a goto to yourself would loop).
 *
 * Exported because the event card opens it directly: at a terminus there is no sensible forward
 * default to write, so cycling to "go" asks where rather than choosing for the teacher.
 */
export function MapPickModal({ tasks, currentTaskId, selectedId, category, special, onPick, onClose }: {
  tasks: Task[];
  currentTaskId: string;
  selectedId: string | null;
  category?: EdgeCategory;
  special?: boolean;
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const exclude = useMemo(() => new Set([currentTaskId]), [currentTaskId]);
  // Open on the task the teacher is editing. Any other node taking focus makes the map draw a flow
  // line to it, so the dialog would open proposing a destination nobody picked — and the first
  // focusable node is the first task on the board, which is how it came to propose exactly that.
  const currentNodeRef = useRef<HTMLButtonElement | null>(null);
  // Centring it is MiniMap's job (`revealCurrent`): the board rescales a paint after it mounts, so
  // a one-shot scroll from here lands against the pre-rescale layout.
  return createPortal(
    <ModalShell title={t('task.flow.card.pickOnMapTitle')} onClose={onClose} maxWidth='max-w-2xl' zClass='z-[120]' initialFocusRef={currentNodeRef}>
      {/* Vertical scrolling belongs to ModalShell's body, which already caps and scrolls. A second
          capped scroller nested inside it meant focus scrolled BOTH only as far as each needed, and
          the current task landed halfway down. Horizontal stays here: the shell scrolls y only, and
          a board with branch columns is wider than the dialog. */}
      <div className='overflow-x-auto rounded-lg border border-ws-border bg-ws-surface p-3'>
        <MiniMap tasks={tasks} mode='pick' currentTaskId={currentTaskId} selectedId={selectedId} excludeIds={exclude} currentNodeRef={currentNodeRef} revealCurrent pickCategory={category} pickSpecial={special} onPick={onPick} />
      </div>
    </ModalShell>,
    document.body,
  );
}
